import React, { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Link } from 'react-router-dom';

import { Loader, SocialContainer } from 'components';
import TextInput from 'components/Inputs/TextInput/TextInputV2';
import { ReactComponent as LeftIcon } from 'static/icons/left.svg';
import { RejectIcon } from 'static';
import { FIELDS } from './form';
import { useForgotPassword } from './hooks';

import {
  StyledButton,
  StyledForm,
  StyledGumbLogo,
  ErrorMessage,
  HeaderWithBack,
  HeaderTitle,
  BackButton,
} from '../styled';

const ForgotPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const {
    register,
    onSubmit,
    errors,
    isLoading,
    authError,
    highlightProviders,
    clearAuthError,
    clearHighlights,
    showSignupCta,
    clearSignupCta,
  } = useForgotPassword();

  // Capture Google Analytics parameters
  const gaParams = useMemo(() => {
    const params = new URLSearchParams();
    for (const [key, value] of searchParams.entries()) {
      if (key.startsWith('_g')) {
        params.append(key, value);
      }
    }
    return params.toString();
  }, [searchParams]);

  const handleBack = () => {
    navigate(`/${gaParams ? `?${gaParams}` : ''}`);
  };

  const signupUrl = `/signup${gaParams ? `?${gaParams}` : ''}`;

  const errorContent = useMemo(() => authError, [authError]);

  return (
    <StyledForm onSubmit={onSubmit}>
      <StyledGumbLogo />
      <HeaderWithBack>
        <BackButton
          type="button"
          onClick={handleBack}
          aria-label={t('common.back')}
        >
          <LeftIcon />
        </BackButton>
        <HeaderTitle>{t('auth.recovery')}</HeaderTitle>
      </HeaderWithBack>
      {authError ? (
        <ErrorMessage>
          <RejectIcon
            width={16}
            height={16}
            aria-hidden="true"
            focusable="false"
            style={{ flex: '0 0 auto' }}
          />
          <span>
            {showSignupCta ? (
              <Trans
                i18nKey="auth.userDoesNotExist"
                components={[
                  <Link
                    key="signup"
                    to={signupUrl}
                    onClick={() => {
                      clearAuthError();
                      clearSignupCta();
                    }}
                  />,
                ]}
              />
            ) : (
              errorContent
            )}
          </span>
        </ErrorMessage>
      ) : null}

      <TextInput
        register={register}
        error={errors[FIELDS.EMAIL]}
        name={FIELDS.EMAIL}
        label={t('auth.labels.email')}
      />
      <StyledButton
        variant={isLoading ? 'secondary' : null}
        disabled={isLoading}
      >
        {isLoading ? (
          <Loader type="button" size="28px" thickness={2} />
        ) : (
          t('auth.send')
        )}
      </StyledButton>
      {highlightProviders.length > 0 ? (
        <SocialContainer
          style={{ marginTop: '60px' }}
          highlightProviders={highlightProviders}
          disabled={isLoading}
          onAuthStart={() => {
            clearAuthError();
            clearSignupCta();
          }}
          iconOnly
        />
      ) : null}
    </StyledForm>
  );
};

export default ForgotPage;
